import React, { useState, useEffect } from 'react';
import { MapPin, Navigation, Building, ArrowRight, Clock, Phone } from 'lucide-react';

const Directions: React.FC = () => {
  const [showStickyBanner, setShowStickyBanner] = useState(false);
  const [hasAnimatedIn, setHasAnimatedIn] = useState(false);

  useEffect(() => {
    // Initial slide-up animation after component mounts
    const initialTimeout = setTimeout(() => {
      setShowStickyBanner(true);
      setHasAnimatedIn(true);
    }, 500);

    const handleScroll = () => {
      const visualDirectionsElement = document.getElementById('visual-directions');
      if (visualDirectionsElement && hasAnimatedIn) {
        const rect = visualDirectionsElement.getBoundingClientRect();
        // Hide banner when visual directions section reaches the top of viewport
        // Show banner when visual directions is below the fold (not yet visible)
        setShowStickyBanner(rect.top > 100);
      }
    };

    window.addEventListener('scroll', handleScroll);
    // Use setTimeout to ensure DOM is fully loaded
    setTimeout(() => handleScroll(), 100);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(initialTimeout);
    };
  }, [hasAnimatedIn]);

  const scrollToVisualDirections = () => {
    const element = document.getElementById('visual-directions');
    if (element) {
      const navHeight = 70; // Height of the nav bar
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - navHeight; // 20px extra padding

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-background pt-[70px]">
      {/* Hero Section */}
      <section className="bg-white py-12 md:py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-headline mb-4">
            Getting to <span className="text-yellowMedium">Kibu Club London</span>
          </h1>
          <p className="text-xl text-gray-700 mb-6">
            Find us at Hackney Depot for your workshop
          </p>
          <div className="flex items-center justify-center gap-2 text-gray-600">
            <MapPin className="w-5 h-5 text-yellowMedium" />
            <span className="text-lg">5 Sheep Ln, London E8 4QS</span>
          </div>
        </div>
      </section>

      {/* Main Directions Section */}
      <section className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Left: Step-by-step directions */}
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-large shadow-sm">
                <div className="flex items-start gap-4 mb-4">
                  <div className="bg-yellowMedium/10 p-3 rounded-hero flex-shrink-0">
                    <Navigation className="w-6 h-6 text-yellowMedium" />
                  </div>
                  <div>
                    <h2 className="font-headline mb-2">Getting There</h2>
                    <p className="text-gray-700 mb-4">
                      Hackney Depot is located in the heart of Hackney, easily accessible by public transport or car.
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="pl-4 border-l-2 border-yellowMedium">
                    <h3 className="font-headline mb-2">By Underground</h3>
                    <p className="text-gray-700">
                      <strong>Cambridge Heath Station</strong> (Overground) - 5 minute walk
                    </p>
                  </div>

                  <div className="pl-4 border-l-2 border-yellowMedium">
                    <h3 className="font-headline mb-2">By Bus</h3>
                    <p className="text-gray-700">
                      Lots of buses stop nearby, with the <strong>394</strong> stopping directly outside
                    </p>
                  </div>

                  <div className="pl-4 border-l-2 border-yellowMedium">
                    <h3 className="font-headline mb-2">By Car</h3>
                    <p className="text-gray-700">
                      Limited street parking available. We recommend using public transport where possible.
                    </p>
                  </div>
                </div>
              </div>

              {/* Finding Us Inside */}
              {/* <div className="bg-yellowMedium/10 p-6 rounded-large">
                <div className="flex items-start gap-4 mb-4">
                  <Building className="w-6 h-6 text-yellowMedium flex-shrink-0" />
                  <div>
                    <h2 className="font-headline mb-2">Finding Us Inside</h2>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="bg-yellowMedium text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-headline">
                      1
                    </div>
                    <div>
                      <p className="text-gray-800">
                        <strong>Enter through the main entrance</strong> on Sheep Lane
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="bg-yellowMedium text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-headline">
                      2
                    </div>
                    <div>
                      <p className="text-gray-800">
                        <strong>Head to the reception area</strong> - our friendly staff will direct you
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="bg-yellowMedium text-white rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 font-headline">
                      3
                    </div>
                    <div>
                      <p className="text-gray-800">
                        <strong>Look for Kibu Club signs</strong> - we'll have clear signage to the workshop space
                      </p>
                    </div>
                  </div>
                </div>
              </div> */}

              {/* Important Info */}
              <div className="bg-white p-6 rounded-large shadow-sm">
                <h3 className="font-headline mb-4 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-yellowMedium" />
                  Important Information
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-yellowMedium mt-1">•</span>
                    <span>Please arrive <strong>10 minutes early</strong> for registration</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellowMedium mt-1">•</span>
                    <span>Workshop starts promptly at <strong>3:00 PM</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-yellowMedium mt-1">•</span>
                    <span>If you're running late, please call us</span>
                  </li>
                </ul>
              </div>

              {/* Contact */}
              <div className="bg-primary text-white p-6 rounded-large">
                <div className="flex items-center gap-3 mb-3">
                  <Phone className="w-5 h-5" />
                  <h3 className="font-headline">Need Help?</h3>
                </div>
                <p className="mb-4">
                  Can't find us or running late? Get in touch and we'll help you out!
                </p>
                <div className="space-y-2">
                  <a
                    href="mailto:club@kibu.family"
                    className="inline-block bg-white text-primary px-6 py-3 rounded-large font-headline hover:shadow-button-hover transition-all duration-300"
                  >
                    Email: club@kibu.family
                  </a>
                </div>
              </div>
            </div>

            {/* Right: Map */}
            <div className="space-y-6">
              <div className="bg-grey rounded-large overflow-hidden h-[600px]">
                <iframe
                  title="Hackney Depot Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2481.4834821668844!2d-0.0609887!3d51.542819299999995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48761cb1c8d6e7e7%3A0x8c3f8e3b8e3b8e3b!2s5%20Sheep%20Ln%2C%20London%20E8%204QS!5e0!3m2!1sen!2suk!4v1234567890123"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

              {/* Quick Actions */}
              <div className="bg-white p-6 rounded-large shadow-sm">
                <h3 className="font-headline mb-4">Quick Actions</h3>
                <div className="space-y-3">
                  <a
                    href="https://www.google.com/maps/dir/?api=1&destination=5+Sheep+Ln+London+E8+4QS"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between w-full bg-yellowMedium text-white px-4 py-3 rounded-large font-headline hover:shadow-button-hover transition-all duration-300"
                  >
                    <span>Get Directions</span>
                    <ArrowRight className="w-5 h-5" />
                  </a>
                  <a
                    href="https://www.google.com/maps/search/?api=1&query=5+Sheep+Ln+London+E8+4QS"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between w-full bg-white text-gray-800 px-4 py-3 rounded-large font-headline border-2 border-grey hover:border-yellowMedium transition-all duration-300"
                  >
                    <span>View on Google Maps</span>
                    <ArrowRight className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Directions - Once You've Arrived */}
      <section id="visual-directions" className="py-12 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block bg-yellowMedium/10 text-yellowMedium px-6 py-3 rounded-full text-sm font-headline mb-4">
              📍 Once You're Here
            </div>
            <h2 className="font-headline mb-4 text-3xl md:text-4xl">How to Find the Space Once Here?</h2>
            <p className="text-lg text-gray-700">
              Follow these photos to navigate inside Hackney Depot
            </p>
          </div>

          <div className="space-y-8">
            {/* Step 1 - Building Exterior */}
            <div className="bg-background p-6 rounded-large">
              <div className="grid md:grid-cols-2 gap-6 items-center">
                <div className="order-2 md:order-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-yellowMedium text-white rounded-full w-10 h-10 flex items-center justify-center font-headline text-xl">
                      1
                    </div>
                    <h3 className="font-headline">Find the Building</h3>
                  </div>
                  <p className="text-gray-700">
                    Look for this building on Sheep Lane. The building has a distinctive brick facade with large windows.
                  </p>
                </div>
                <div className="order-1 md:order-2">
                  <div className="bg-grey rounded-large overflow-hidden aspect-video">
                    <img
                      src="https://d1tg5gdu842vo5.cloudfront.net/site-assets/images/kibu-club/139d0b38-f382-4922-94df-fd8c13cf2852%20Large.jpeg"
                      alt="Hackney Depot building exterior"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 - Gates */}
            <div className="bg-background p-6 rounded-large">
              <div className="grid md:grid-cols-2 gap-6 items-center">
                <div className="order-2">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-yellowMedium text-white rounded-full w-10 h-10 flex items-center justify-center font-headline text-xl">
                      2
                    </div>
                    <h3 className="font-headline">Enter Through the Gates</h3>
                  </div>
                  <p className="text-gray-700">
                    Go through these gates to access the building entrance.
                  </p>
                </div>
                <div className="order-1">
                  <div className="bg-grey rounded-large overflow-hidden aspect-video">
                    <img
                      src="https://d1tg5gdu842vo5.cloudfront.net/site-assets/images/kibu-club/25687f43-0f01-413d-8fa5-8e761c97e0f4%20Large.jpeg"
                      alt="Entry gates"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 - Lift/Stairs */}
            <div className="bg-background p-6 rounded-large">
              <div className="grid md:grid-cols-2 gap-6 items-center">
                <div className="order-2 md:order-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-yellowMedium text-white rounded-full w-10 h-10 flex items-center justify-center font-headline text-xl">
                      3
                    </div>
                    <h3 className="font-headline">Take the Lift or Stairs</h3>
                  </div>
                  <div className="space-y-3 text-gray-700">
                    <p className="font-headline text-sm">
                      <strong>Using the lift:</strong>
                    </p>
                    <p>
                      Take the lift to the <strong>second floor</strong>.
                    </p>
                    <p className="font-headline text-sm mt-3">
                      <strong>Using the stairs:</strong>
                    </p>
                    <p>
                      Press the <strong>release button</strong> facing opposite the red door and lift to access the stairs. Go to the second floor.
                    </p>
                  </div>
                </div>
                <div className="order-1 md:order-2">
                  <div className="bg-grey rounded-large overflow-hidden aspect-video">
                    <img
                      src="https://d1tg5gdu842vo5.cloudfront.net/site-assets/images/kibu-club/9aeadc89-0e0c-4c51-a227-67473f70a4ba%20Large.jpeg"
                      alt="Lift and stairs entrance with red door"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4 - Corridor */}
            <div className="bg-background p-6 rounded-large">
              <div className="grid md:grid-cols-2 gap-6 items-center">
                <div className="order-2">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-yellowMedium text-white rounded-full w-10 h-10 flex items-center justify-center font-headline text-xl">
                      4
                    </div>
                    <h3 className="font-headline">Exit Lift and Turn Left</h3>
                  </div>
                  <p className="text-gray-700">
                    As you exit the lift, turn <strong>left</strong> and you'll see this corridor. Go through the <strong>first set of doors</strong>.
                  </p>
                </div>
                <div className="order-1">
                  <div className="bg-grey rounded-large overflow-hidden aspect-video">
                    <img
                      src="https://d1tg5gdu842vo5.cloudfront.net/site-assets/images/kibu-club/09ddb728-0378-4cc9-bc43-536098a8d611%20Large.jpeg"
                      alt="Corridor from lift exit"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Step 5 - Main Hall */}
            <div className="bg-background p-6 rounded-large">
              <div className="grid md:grid-cols-2 gap-6 items-center">
                <div className="order-2 md:order-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-yellowMedium text-white rounded-full w-10 h-10 flex items-center justify-center font-headline text-xl">
                      5
                    </div>
                    <h3 className="font-headline">Welcome to the Main Hall!</h3>
                  </div>
                  <p className="text-gray-700">
                    Walk all the way through until you reach the main hall. Look for our Kibu Club team - we'll be ready to welcome you!
                  </p>
                </div>
                <div className="order-1 md:order-2">
                  <div className="bg-grey rounded-large overflow-hidden aspect-video">
                    <img
                      src="https://d1tg5gdu842vo5.cloudfront.net/site-assets/images/kibu-club/9acdb641-a91f-43d3-9ae0-29c42e7474ef%20Large.jpeg"
                      alt="Main hall entrance"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Still Lost? */}
          <div className="mt-12 bg-yellowMedium/10 p-8 rounded-large text-center">
            <h3 className="font-headline mb-3">Still Can't Find Us?</h3>
            <p className="text-gray-700 mb-4">
              Don't worry! Give us a call and we'll come get you.
            </p>
            <a
              href="mailto:club@kibu.family"
              className="inline-block bg-yellowMedium text-white px-8 py-3 rounded-large font-headline hover:shadow-button-hover transition-all duration-300"
            >
              Contact Us: club@kibu.family
            </a>
          </div>
        </div>
      </section>

      {/* What3Words Section (Optional) */}
      <section className="py-12 px-6 bg-background">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-headline mb-4">Using What3Words?</h2>
          <p className="text-lg text-gray-700 mb-4">
            Find us at: <strong className="text-yellowMedium">///stand.awards.zeal</strong>
          </p>
          <p className="text-sm text-gray-600">
            What3Words is a precise location system that divides the world into 3m x 3m squares
          </p>
        </div>
      </section>

      {/* Sticky Bottom Banner */}
      <div
        className={`fixed bottom-0 left-0 right-0 bg-yellowMedium shadow-lg z-50 transition-transform duration-500 ease-in-out ${showStickyBanner ? 'translate-y-0' : 'translate-y-full'
          }`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between gap-4 flex-col sm:flex-row text-center sm:text-left">
            <div className="flex-1">
              <h3 className="font-headline text-white text-lg md:text-2xl mb-1">
                Already Here? Find the Space Inside
              </h3>
              {/* <p className="text-white/90 text-sm">
                View photo directions to navigate inside Hackney Depot
              </p> */}
            </div>
            <button
              onClick={scrollToVisualDirections}
              className="bg-white text-yellowMedium px-6 py-3 rounded-large font-headline hover:shadow-button-hover transition-all duration-300 transform hover:-translate-y-1 whitespace-nowrap"
            >
              View Directions →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Directions;
